import { queryCache, useInfiniteQuery, useQuery } from 'react-query';
import {useLang} from '../config/TranslationProvider';

const escapeId = id => encodeURIComponent(id);

/**
 * Get image url
 */
export const getMediaUrl = (id, context = 'event', size = 'large', imageType = 'teaser', fileType = 'jpg') => {
    let basePath = window.APP_CONFIG.MEDIA_BASE_PATH;
    if (window.APP_CONFIG.MEDIA_BASE_PATH.endsWith('/')) {
        basePath = basePath.slice(0, -1);
    }

    // /media/show/1234/event_teaser_large.jpg
    return `${basePath}/${id}/${context}_${imageType}_${size}.${fileType}`;
};

export const getVideoURL = (mediaItem) => {
    let url;
    switch (mediaItem.provider_name)
    {
        case 'vimeo':
            url = 'www.vimeo.com/' + mediaItem.video_id;
            break;
        case 'youtube':
            url = 'www.youtube.com/watch?v=' + mediaItem.video_id;
            break;
        default:
            url = '';
    }

    return url;
};

function fetchFromApi(endpoint, parameters = {}) {
    // Build url with parameters
    let entrypoint = window.APP_CONFIG.API_ENTRYPOINT;
    if (entrypoint.endsWith('/')) {
        entrypoint = entrypoint.slice(0, -1);
    }
    const url = new URL(endpoint, `${entrypoint}/api/v1/`);

    // Add query parameters
    for (const key of Object.keys(parameters)) {
        let value = parameters[key];
        if (typeof value !== 'string') {
            value = JSON.stringify(value);
        }

        url.searchParams.append(key, value);
    }

    // Create abort controller to cancel this request when needed
    const controller = new AbortController();
    const signal = controller.signal;

    // Do fetch
    const promise = fetch(url, {
        headers: {
            'Accept': 'application/json',
            'api-authorisation-key': window.APP_CONFIG.API_KEY,
        },
        signal,
    }).then(response => response.json());

    // Abort request if react-query calls the `promise.cancel` method
    promise.cancel = () => controller.abort();

    return promise;
}

// Turns tags into tags_title as this key is different for the events api (has legacy reasons)
function fixEventFilter(filter) {
    const fixedFilter = JSON.parse(JSON.stringify(filter));

    if (fixedFilter.tags) {
        fixedFilter.tags_title = fixedFilter.tags;
        delete fixedFilter.tags;
    }

    // Still support tag ids coming from backend api for the predefined filters
    if (fixedFilter.tags_id) {
        fixedFilter.tags = fixedFilter.tags_id;
        delete fixedFilter.tags_id;
    }

    if (fixedFilter.hosts?.length === 0) {
        delete fixedFilter.hosts;
    }

    return fixedFilter;
}

/**
 * Gets a list of events.
 */
export const useEvents = (filter, limit = 20, offset = 0, queryOptions) => useQuery(
    ['events', {filter: fixEventFilter(filter), offset, limit}],
    fetchFromApi,
    queryOptions,
);

/**
 * Gets a list of events. As infinite query.
 */
export const useInfiniteEvents = (filter, limit = 20) => useInfiniteQuery(
    ['events', {filter: fixEventFilter(filter), limit}],
    (endpoint, parameters, offset = 0) => fetchFromApi(endpoint, {
        ...parameters,
        offset,
    }),
    {
        getFetchMore: lastEventGroup => {
            const newOffset = lastEventGroup.offset + lastEventGroup.limit;

            return newOffset < lastEventGroup.total_count ? newOffset : false;
        },
    },
);

/**
 * Get an event by its id.
 */
export const useEvent = (id, params = {}) => {
    const { lang } = useLang();

    return useQuery([`events/${escapeId(id)}`, {
        ...params,
        lang,
    }], fetchFromApi);
};

/**
 * Gets the event dates for a specific event. As infinite query.
 */
export const useInfiniteEventDates = (eventId, filter = {}, limit = 100) => useInfiniteQuery(
    ['eventdates', {
        filter: {
            event_id: eventId,
            ...fixEventFilter(filter),
        },
        limit,
        include_event: false,
    }],
    (endpoint, parameters, offset = 0) => fetchFromApi(endpoint, {
        ...parameters,
        offset,
    }),
    {
        getFetchMore: lastEventDateGroup => {
            const newOffset = lastEventDateGroup.offset + lastEventDateGroup.limit;

            return newOffset < lastEventDateGroup.total_count ? newOffset : false;
        },
    },
);

/**
 * Get an host by its id.
 */
export const useHost = id => {
    const {lang} = useLang();

    return useQuery([`hosts/${escapeId(id)}`, {lang}], fetchFromApi);
};

/**
 * Gets a list of locations.
 */
export const useLocations = (filter, limit = 20, offset = 0, queryOptions) => useQuery(
    ['locations', {filter, offset, limit}],
    fetchFromApi,
    queryOptions,
);

/**
 * Gets a list of locations. As infinite query.
 */
export const useInfiniteLocations = (filter, limit = 20) => {
    const {data, isStale, ...rest} = useInfiniteQuery(
        ['locations', {filter, limit}],
        (endpoint, parameters, offset = 0) => fetchFromApi(endpoint, {
            ...parameters,
            offset,
        }),
        {
            getFetchMore: lastEventGroup => {
                // For some reason, the locations apis returns strings instead of integers for offset and limit
                const newOffset = parseInt(lastEventGroup.offset, 10) + parseInt(lastEventGroup.limit, 10);

                return newOffset < lastEventGroup.total_count ? newOffset : false;
            },
        },
    );

    // Add events to cache
    if (!isStale && data[0] && data[0].data) {
        for (const locationGroup of data) {
            for (const location of locationGroup.data) {
                queryCache.setQueryData(`locations/${escapeId(location.id)}`, location);
            }
        }
    }

    return {data, ...rest};
};

/**
 * Get an location by its id.
 */
export const useLocation = id => {
    const {lang} = useLang();

    return useQuery([`locations/${escapeId(id)}`, {lang}], fetchFromApi);
};

/**
 * Get all categories
 */
export const useCategories = () => {
    const {data, ...rest} = useQuery('categories', fetchFromApi);

    // Add category to cache
    if (data) {
        for (const category of data) {
            queryCache.setQueryData(`categories/${escapeId(category.id)}`, category);
        }
    }

    // Return the normal query data
    return {data, ...rest};
};

/**
 * Get an category by its id.
 */
export const useCategory = id => useQuery(`categories/${escapeId(id)}`, fetchFromApi);

/**
 * Get all filters
 */
export const useFilter = () => {
    const {lang} = useLang();
    const {data, ...rest} = useQuery(['filter', {lang}], fetchFromApi);

    // Add filter to cache
    if (data) {
        for (const filter of data) {
            queryCache.setQueryData(`filter/${escapeId(filter.id)}`, filter);
        }
    }

    // Return the normal query data
    return {data, ...rest};
};

/**
 * Get all active banners.
 */
export const useBanners = () => {
    const {lang} = useLang();

    return useQuery(['banners', {lang}], fetchFromApi);
};

/**
 * Get all active pages.
 */
export const usePages = () => {
    const { lang } = useLang();

    return useQuery(['pages', {
        lang,
    }], fetchFromApi);
};

/**
 * Get an active page by its path.
 */
export const usePage = path => {
    const { lang } = useLang();

    return useQuery([`pages/${path}`, {
        lang,
    }], fetchFromApi);
};

/**
 * Get the images for the homepage slider
 */
export const useHeaders = () => useQuery('headers', fetchFromApi);
